import React, { useState } from 'react';
import {
  MapPin,
  Users,
  Eye,
  Edit,
  Copy,
  Laptop,
  GraduationCap,
  Calendar,
  Globe,
  Building2,
  Sparkles,
  ChevronRight,
  Check,
  Award,
  CalendarDays,
  MapPinned,
  WifiOff,
  PartyPopper
} from 'lucide-react';

const EventCard = ({
  event,
  onView,
  onEdit,
  onShare,
  userRole
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  // Safe string conversion for IDs
  const getIndex = () => {
    if (!event?.Event_Id) return 0;
    const idStr = String(event.Event_Id);
    return idStr.charCodeAt(0) % 3;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return { date: 'N/A', time: 'N/A' };

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let dateStr;
      if (date.toDateString() === today.toDateString()) {
        dateStr = 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        dateStr = 'Tomorrow';
      } else {
        dateStr = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }

      return {
        date: dateStr,
        time: date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        fullDate: date,
        isToday: date.toDateString() === today.toDateString(),
        isTomorrow: date.toDateString() === tomorrow.toDateString()
      };
    } catch (error) {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  const getStatusInfo = (statusId) => {
    const statusConfig = {
      'Active': {
        name: 'Live Now',
        badge: 'bg-green-600 text-white',
        headerColor: 'bg-green-600',
        lightBg: 'bg-green-50',
        lightText: 'text-green-700',
        icon: <Sparkles size={12} className="animate-pulse" />,
        dotColor: 'bg-green-500',
        pingColor: 'bg-green-400',
        borderColor: 'border-green-200',
        textColor: 'text-green-600',
        shadowColor: 'green'
      },
      'Upcoming': {
        name: 'Upcoming',
        badge: 'bg-blue-600 text-white',
        headerColor: 'bg-blue-600',
        lightBg: 'bg-blue-50',
        lightText: 'text-blue-700',
        icon: <CalendarDays size={12} />,
        borderColor: 'border-blue-200',
        textColor: 'text-blue-600',
        shadowColor: 'blue'
      },
      'Completed': {
        name: 'Completed',
        badge: 'bg-gray-600 text-white',
        headerColor: 'bg-gray-700',
        lightBg: 'bg-gray-50',
        lightText: 'text-gray-700',
        icon: <Award size={12} />,
        borderColor: 'border-gray-200',
        textColor: 'text-gray-600',
        shadowColor: 'gray'
      },
      'Cancelled': {
        name: 'Cancelled',
        badge: 'bg-red-600 text-white',
        headerColor: 'bg-red-700',
        lightBg: 'bg-red-50',
        lightText: 'text-red-700',
        icon: <WifiOff size={12} />,
        borderColor: 'border-red-200',
        textColor: 'text-red-600',
        shadowColor: 'red'
      }
    };
    return statusConfig[statusId] || {
      name: statusId || 'Unknown',
      badge: 'bg-gray-600 text-white',
      headerColor: 'bg-gray-600',
      lightBg: 'bg-gray-50',
      lightText: 'text-gray-700',
      icon: <Calendar size={12} />,
      borderColor: 'border-gray-200',
      textColor: 'text-gray-600',
      shadowColor: 'gray'
    };
  };

  const getEventModeIcon = (modeId) => {
    const modeNum = Number(modeId);

    const iconMap = {
      1: { icon: <Laptop size={14} />, label: 'Online', color: 'bg-gray-50 text-gray-700', border: 'border-gray-200', bg: 'bg-gray-500' },
      2: { icon: <Building2 size={14} />, label: 'In-person', color: 'bg-gray-50 text-gray-700', border: 'border-gray-200', bg: 'bg-gray-500' },
      3: { icon: <Globe size={14} />, label: 'Hybrid', color: 'bg-gray-50 text-gray-700', border: 'border-gray-200', bg: 'bg-gray-500' },
    };
    return iconMap[modeNum] || {
      icon: <MapPin size={14} />,
      label: modeId || 'Unknown',
      color: 'bg-gray-50 text-gray-700',
      border: 'border-gray-200',
      bg: 'bg-gray-500'
    };
  };

  // Calculate total max participants
  const getTotalParticipants = () => {
    if (!event?.MaxParticipants) return 0;
    try {
      const participants = String(event.MaxParticipants).split(',');
      return participants.reduce((sum, val) => sum + (Number(val) || 0), 0);
    } catch {
      return 0;
    }
  };
  // Safe data extraction with defaults
  const startTime = formatDateTime(event?.Start_datetime);
  const endTime = formatDateTime(event?.End_datetime);
  const statusInfo = getStatusInfo(event?.Event_Status_Word);
  const eventMode = getEventModeIcon(event?.Event_mode);
  const totalParticipants = getTotalParticipants();
  const index = getIndex();

  // Fallback header colors for index-based variation (only used if status doesn't have a specific header)
  const headerColors = [
    'bg-blue-600',
    'bg-blue-500',
    'bg-blue-700',
  ];

  // Use status-based header color, fallback to index-based if not available
  const headerColor = statusInfo.headerColor || (headerColors[index] || 'bg-blue-600');
  const isLive = statusInfo.name === 'Live Now';
  const isCompleted = statusInfo.name === 'Completed';

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/event/${event?.Event_Id || ''}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onShare?.(event);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Don't render if no event data
  if (!event) return null;

  return (
    <div
      className="group relative h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Simple shadow on hover - color based on status */}
      <div className={`absolute -inset-0.5 ${statusInfo.headerColor} rounded-3xl blur-xl opacity-0 group-hover:opacity-10 transition duration-500`}></div>

      {/* Main Card */}
      <article className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-500 transform h-full flex flex-col ${isHovered
          ? `scale-[1.02] shadow-2xl shadow-${statusInfo.shadowColor}-600/20`
          : `shadow-lg hover:shadow-xl shadow-${statusInfo.shadowColor}-600/10`
        } border ${statusInfo.borderColor}`}>

        {/* Header - Status-based color */}
        <div className={`relative ${headerColor} px-6 pt-6 pb-12 flex-shrink-0`}>
          {/* Decorative Circles - White with opacity */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-8 -mt-8"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-8 -mb-8"></div>

          {/* Status Badge - Positioned in header */}
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${statusInfo.badge} shadow-lg`}>
              {statusInfo.icon && <span className="mr-1.5">{statusInfo.icon}</span>}
              {statusInfo.name}
            </span>
          </div>

          {/* Title in Header - White text for better visibility */}
          <h3 className="font-bold text-white text-xl leading-tight pr-12 line-clamp-2 drop-shadow-md">
            {event?.Event_Name || 'Untitled Event'}
          </h3>

          {/* Institution in Header - Light colored text */}
          <div className={`flex items-center ${isLive ? 'text-green-100' : statusInfo.name === 'Completed' ? 'text-gray-100' : statusInfo.name === 'Cancelled' ? 'text-red-100' : 'text-blue-100'} mt-2`}>
            <GraduationCap size={14} className="mr-2 flex-shrink-0" />
            <span className="text-sm font-medium truncate max-w-[180px]">{event?.Inst_Name || 'Institution'}</span>
            {(event?.Inst_Name && event?.Div_Name) && (
              <>
                <span className={`mx-2 ${isLive ? 'text-green-300' : statusInfo.name === 'Completed' ? 'text-gray-300' : statusInfo.name === 'Cancelled' ? 'text-red-300' : 'text-blue-300'}`}>•</span>
                <span className={`text-sm ${isLive ? 'text-green-200' : statusInfo.name === 'Completed' ? 'text-gray-200' : statusInfo.name === 'Cancelled' ? 'text-red-200' : 'text-blue-200'} truncate max-w-[140px]`}>{event?.Div_Name}</span>
              </>
            )}
          </div>

          {/* Event Icon in Circle - Positioned overlapping header and content */}
          <div className="absolute -bottom-7 right-6">
            <div className="relative">
              <div className={`w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300 border-2 ${statusInfo.borderColor}`}>
                <div className={`${statusInfo.textColor}`}>
                  <CalendarDays size={28} />
                </div>
              </div>
              {/* Status Indicator Dot - Only for Live events */}
              {statusInfo.name === 'Live Now' && (
                <div className="absolute -top-1 -right-1 w-4 h-4">
                  <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></div>
                  <div className="relative w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content - Flex grow to fill space */}
        <div className="pt-10 p-6 flex flex-col flex-grow">
          {/* Description with Background on Hover - color based on status */}
          <div className="relative mb-6">
            <div className={`absolute -inset-2 ${statusInfo.headerColor} rounded-xl opacity-0 group-hover:opacity-5 blur transition duration-500`}></div>
            <p className="relative text-gray-600 text-sm leading-relaxed line-clamp-2">
              {event?.Event_Description || 'No description provided.'}
            </p>
          </div>

          {/* Event Details Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Date & Time */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-lg ${statusInfo.lightBg} flex items-center justify-center`}>
                  <Calendar size={16} className={statusInfo.textColor} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Date</div>
                <div className="font-semibold text-sm text-gray-900 truncate">{startTime.date}</div>
                <div className="text-xs text-gray-500 truncate">{startTime.time} - {endTime.time}</div>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-lg ${statusInfo.lightBg} flex items-center justify-center`}>
                  <MapPinned size={16} className={statusInfo.textColor} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Venue</div>
                <div className="font-semibold text-sm text-gray-900 truncate">
                  {event?.Event_Place || 'TBA'}
                </div>
              </div>
            </div>

            {/* Participants */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-lg ${statusInfo.lightBg} flex items-center justify-center`}>
                  <Users size={16} className={statusInfo.textColor} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Capacity</div>
                <div className="font-semibold text-sm text-gray-900 truncate">
                  {totalParticipants === 0 ? 'Unlimited' : `${totalParticipants} spots`}
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${eventMode.color} bg-opacity-10 border ${eventMode.border}`}>
              {eventMode.icon}
              <span className="ml-1.5">{eventMode.label}</span>
            </span>
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${statusInfo.lightBg} ${statusInfo.lightText}`}>
              <PartyPopper size={14} />
              <span className="ml-1.5">{event?.Event_type_Name || 'Event'}</span>
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t ${statusInfo.borderColor} mt-auto">
            {/* View Details Button - Color based on status */}
            {onView && (
              <button
                onClick={() => onView(event)}
                className={`group/btn relative overflow-hidden inline-flex items-center px-5 py-2.5 rounded-xl ${statusInfo.badge
                  } font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl`}
              >
                <span className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-700"></span>
                <Eye size={16} className="mr-2" />
                <span className="hidden sm:inline">View Details</span>
                <span className="sm:hidden">View</span>
                <ChevronRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </button>
            )}

            {/* Action Icons - Only show for non-completed events */}
            {!isCompleted && (
              <div className="flex gap-1.5">
                {onShare && userRole == "Administrator" && (
                  <button
                    onClick={handleCopyLink}
                    className={`relative p-2.5 rounded-xl ${statusInfo.lightBg} ${statusInfo.textColor} hover:bg-opacity-80 transition-all duration-300 group/btn`}
                    title="Copy Registration Link"
                  >
                    {copied ? (
                      <>
                        <Check size={18} className={`${statusInfo.textColor} animate-scale`} />
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none">
                          Copied!
                        </span>
                      </>
                    ) : (
                      <>
                        <Copy size={18} />
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/btn:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Copy Link
                        </span>
                      </>
                    )}
                  </button>
                )}

                {/* {onEdit && userRole == "Administrator" && ( */}
                  <button
                    onClick={() => onEdit(event)}
                    className={`relative p-2.5 rounded-xl ${statusInfo.lightBg} ${statusInfo.textColor} hover:bg-opacity-80 transition-all duration-300 group/btn`}
                    title="Edit Event"
                  >
                    <Edit size={18} />
                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/btn:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Edit
                    </span>
                  </button>
                {/* )} */}
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default EventCard;